import React, { FC, memo, NamedExoticComponent, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  IconButton,
  Typography,
} from '@mui/material'
import { CloseIcon } from '../../../icons/CloseIcon'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { formatTimeInJP, formatTimeInJPFull } from '../../../helpers'
import { Prescription } from '../../../types/models/Presc'
import { localStorageWrapper } from '../../../helpers/localStorage'
import { useAppType } from '../../../contexts/AppTypeContext'
import { SearchModal } from './SearchModal'

type Props = {
  isOpen: boolean
  onClose: () => void
  prescriptions: Prescription[]
  selectedPresc?: Prescription
  selectOcrRequest: (ocrRequestEvent: string) => void
  BottomButtons: React.ReactNode[]
}

export const MelphinDrawerPrescList: FC<Props> = ({
  isOpen,
  onClose,
  prescriptions,
  selectedPresc,
  selectOcrRequest,
  BottomButtons,
}) => {
  const { config } = useAppType()
  const serviceName = config.serviceName
  const theme = useTheme()

  // 日付選択と検索機能の状態管理
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  // 処方箋の日付範囲を計算
  const dateRange = useMemo(() => {
    if (prescriptions.length === 0) {
      return { minDate: new Date(), maxDate: new Date(), uniqueDates: 1 }
    }

    const dates = prescriptions.map((presc) => {
      const date = new Date(presc.request_event_created_at)
      // 日付のみを比較するため、時刻をリセット
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    })

    const uniqueDatesSet = new Set(dates.map((d) => d.toDateString()))
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())))
    const maxDate = new Date() // 今日まで

    return {
      minDate,
      maxDate,
      uniqueDates: uniqueDatesSet.size,
    }
  }, [prescriptions])

  // 日付選択UIを表示するかどうか（複数日の処方箋がある場合のみ表示）
  const showDateSelector = dateRange.uniqueDates > 1

  // 日付でフィルタリングされた処方箋リスト
  const dateFilteredPrescriptions = useMemo(() => {
    return prescriptions.filter((presc) => {
      const prescDate = new Date(presc.request_event_created_at)
      return (
        prescDate.getFullYear() === selectedDate.getFullYear() &&
        prescDate.getMonth() === selectedDate.getMonth() &&
        prescDate.getDate() === selectedDate.getDate()
      )
    })
  }, [prescriptions, selectedDate])

  // 検索用アイテムの準備
  const searchItems = useMemo(() => {
    return prescriptions.map((presc) => ({
      id: presc.ocr_request_event_id,
      label: presc.label,
      created_at: presc.request_event_created_at,
    }))
  }, [prescriptions])

  const handleDateBack = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(selectedDate.getDate() - 1)
    // 最小日付より前には戻らない
    if (newDate >= dateRange.minDate) {
      setSelectedDate(newDate)
    }
  }

  const handleDateForward = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(selectedDate.getDate() + 1)
    // 今日より先には進まない
    if (newDate <= dateRange.maxDate) {
      setSelectedDate(newDate)
    }
  }

  const isToday = selectedDate.toDateString() === new Date().toDateString()
  const isMinDate =
    selectedDate.toDateString() === dateRange.minDate.toDateString()

  const formatDate = (date: Date) => {
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}/${String(date.getDate()).padStart(2, '0')}`
  }

  return (
    <>
      <Drawer
        anchor={'left'}
        open={isOpen}
        onClose={onClose}
        sx={{
          position: 'relative',
          '& .MuiDrawer-paper': {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
        style={{
          opacity: 0.9,
          zIndex: 1301,
        }}
      >
        <Stack direction="column">
          <Box
            sx={{ height: '5vh' }}
            left={0}
            top={0}
            bgcolor={'primary.dark'}
            color="#fff"
            p={1}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button
                onClick={onClose}
                startIcon={<CloseIcon />}
                style={{ color: '#fff' }}
              >
                {selectedPresc ? selectedPresc.label : serviceName}
              </Button>
              <IconButton onClick={() => setIsSearchModalOpen(true)}>
                <SearchIcon style={{ color: '#fff' }} />
              </IconButton>
            </Stack>
          </Box>

          {/* 日付選択UI - 日付は常に表示、ボタンは複数日の処方箋がある場合のみ表示 */}
          <Box bgcolor={'primary.dark'} p={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
            >
              {showDateSelector ? (
                <IconButton
                  onClick={handleDateBack}
                  disabled={isMinDate}
                  size="small"
                >
                  <ArrowBackIosNewIcon
                    style={{
                      color: isMinDate ? '#666' : '#fff',
                      fontSize: 18,
                    }}
                  />
                </IconButton>
              ) : (
                <Box width={40} />
              )}

              <Typography fontSize={16} color="#fff">
                {formatDate(selectedDate)}
              </Typography>

              {showDateSelector ? (
                <IconButton
                  onClick={handleDateForward}
                  disabled={isToday}
                  size="small"
                >
                  <ArrowForwardIosIcon
                    style={{
                      color: isToday ? '#666' : '#fff',
                      fontSize: 18,
                    }}
                  />
                </IconButton>
              ) : (
                <Box width={40} />
              )}
            </Stack>
          </Box>

          <Box
            overflow="scroll"
            bgcolor={'primary.dark'}
            color="#fff"
            sx={{ width: 200, height: '75vh' }}
            role="presentation"
          >
            <List>
              {selectedPresc &&
                dateFilteredPrescriptions.map((presc) => (
                  <DrawerItem
                    key={presc.ocr_request_event_id}
                    prescription={presc}
                    selectOcrRequest={selectOcrRequest}
                    isSelected={presc.id === selectedPresc.id}
                  />
                ))}
            </List>
          </Box>

          {BottomButtons.map((button, index) => (
            <Stack
              key={index}
              p={1}
              sx={{
                position: 'fixed',
                bottom: `${(BottomButtons.length - 1 - index) * 7}vh`,
                width: 184,
                height: '5vh',
                borderTop: '1px solid #fff',
                justifyContent: 'center',
              }}
              bgcolor={theme.palette.primary.dark}
            >
              {button}
            </Stack>
          ))}
        </Stack>
      </Drawer>

      {/* 検索モーダル */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        items={searchItems}
        onItemSelect={(id) => {
          selectOcrRequest(id)
          setIsSearchModalOpen(false)
          onClose()
        }}
        formatDate={(date) => {
          const d = new Date(date)
          return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(
            2,
            '0'
          )}/${String(d.getDate()).padStart(2, '0')} ${String(
            d.getHours()
          ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        }}
      />
    </>
  )
}

type DrawerProps = {
  prescription: Prescription
  selectOcrRequest: (ocrRequestEvent: string) => void
  isSelected: boolean
}

const DrawerItem: NamedExoticComponent<DrawerProps> = memo(
  ({ prescription, selectOcrRequest, isSelected }) => {
    const isAnnotator = localStorageWrapper.getItem('is_annotator') || false
    const theme = useTheme()
    return (
      <ListItem
        component="li"
        onClick={() => selectOcrRequest(prescription.ocr_request_event_id)}
        key={prescription.ocr_request_event_id}
        style={{
          cursor: 'pointer',
          ...(isSelected
            ? {
                backgroundColor: '#fff',
                color: theme.palette.primary.dark,
              }
            : { borderTop: '0.5px solid #fff' }),
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          width="100%"
        >
          <Box>
            <ListItemText primary={prescription.label} />
          </Box>
          <Box>
            <ListItemText
              primary={
                isAnnotator
                  ? formatTimeInJPFull(prescription.request_event_created_at)
                  : formatTimeInJP(prescription.request_event_created_at)
              }
            />
          </Box>
        </Stack>
      </ListItem>
    )
  },
  (prev, next) => {
    return (
      prev.prescription.label === next.prescription.label &&
      prev.prescription.request_event_created_at ===
        next.prescription.request_event_created_at &&
      prev.isSelected === next.isSelected
    )
  }
)

DrawerItem.displayName = 'DrawerItem'
